import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { faSearch, faXmark } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { COLORS } from '../../../design-system/settings/variables/Colors';

interface StyledProps {
  size: SearchFieldProps['size'];
  maxWidth: SearchFieldProps['maxWidth']; // in `px`
}

const StyledDiv = styled.div`
  input {
    border: none;
    outline: none;
    width: 100%;
    height: 100%;
  }
  button {
    border: none;
    outline: none;
    cursor: pointer;
    background-color: inherit;
    &:focus {
      outline: auto;
    }
  }
  display: flex;
  gap: 8px;
  width: 100%;
  align-items: center;
  background-color: ${COLORS.whiteBase};
  border-radius: 4px;
  height: ${({ size }: StyledProps) => (size === 'large' ? 56 : 42)}px;
  padding: ${({ size }: StyledProps) => (size === 'large' ? 16 : 8)}px;
  ${({ maxWidth }: StyledProps) => (maxWidth ? `max-width: ${maxWidth}px` : '')}
`;

interface SearchFieldProps {
  maxWidth?: number; // in `px`
  value: string | undefined;
  name?: string;
  placeholder: string;
  size: 'small' | 'large';
  autoFocus?: boolean;
  className?: string;
  onChange?: (value: string, name?: string) => unknown;
  onBlur?: (value: string, name?: string) => unknown;
  onEnter?: (value: string, name?: string) => unknown;
}

const SearchField: React.FC<SearchFieldProps> = ({
  value,
  placeholder,
  onChange,
  onBlur,
  onEnter,
  className,
  size,
  name,
  maxWidth,
  autoFocus,
}) => {
  const ref = React.createRef<HTMLInputElement>();
  const [showClear, setShowClear] = useState<boolean>(Boolean(value));

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setShowClear(Boolean((e.target as any).value));
      if (!onEnter || !ref.current || e.key !== 'Enter') return;
      onEnter(ref.current.value, name);
    },
    [onEnter, name, ref.current, setShowClear],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value, name } = e.target;
      onChange?.(value, name);
    },
    [onChange],
  );

  const handleClear = useCallback(() => {
    onChange?.('', name);
    if (!ref.current) return;
    ref.current.focus();
    ref.current.value = '';
    setShowClear(false);
  }, [onChange, name, ref, setShowClear]);

  const handleBlur = useCallback(() => {
    onBlur?.(ref.current?.value || '', name);
  }, [ref.current, name, onBlur]);

  return (
    <StyledDiv maxWidth={maxWidth} size={size} className={className}>
      <FontAwesomeIcon icon={faSearch} />
      <input
        ref={ref}
        name={name}
        value={value}
        onChange={onChange ? handleChange : undefined}
        onBlur={handleBlur}
        onKeyPress={handleKeyPress}
        placeholder={placeholder}
        autoFocus={autoFocus}
      />
      {showClear && (
        <button onClick={handleClear}>
          <FontAwesomeIcon icon={faXmark} />
        </button>
      )}
    </StyledDiv>
  );
};

export default React.memo(SearchField);
